<template>
  <b-form id="create-app-form" @submit.prevent="submitForm">
    <b-form-group>
      <template v-slot:label>{{ $t("appName") }} <span class="text-danger">*</span></template>
      <b-form-input type="text" v-model="name" required minlength="2" maxlength="100" />
    </b-form-group>
    <b-form-group>
      <template v-slot:label>Domain <span class="text-danger">*</span></template>
      <b-form-input type="text" v-model="domainName" required minlength="3" maxlength="200" />
    </b-form-group>
    <b-form-group>
      <template v-slot:label>Logo <span class="text-danger">*</span></template>
      <div v-if="logo" class="logo-container bg-primary mb-2">
        <img :src="logo" alt="logo">
      </div>
      <b-form-file v-model="logoFile" @input="onFileInput" required accept="image/*" plain />
    </b-form-group>

    <b-form-group :label="$t('customer')" :hidden="selectedCustomer">
      <v-select class="vs-normalizer" :clearable="false" :filterable="false"
                :options="customerTypeaheadOptions"
                @search="doSearchCustomerDebounced" @option:selected="onSelectCustomer">
        <template v-slot:search="{attributes, events}">
          <input id="input-name" v-bind="attributes" v-on="events" class="form-control" :placeholder="$t('customerAutofillPlaceholder')"/>
        </template>
      </v-select>
    </b-form-group>
    <b-form-group :label="$t('customer')" v-if="selectedCustomer">
      <b-input-group>
        <b-form-input type="text" readonly v-model="selectedCustomer.label" />
        <b-input-group-append>
          <b-input-group-text class="cursor-pointer" @click="selectedCustomer = null">
            <b-icon-x />
          </b-input-group-text>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>

    <div class="text-right pt-3">
      <b-button type="button" variant="secondary" class="mr-3" @click="onClickCancel" :disabled="isSubmitting">{{ $t("cancel") }}</b-button>
      <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("createApp") }}</b-button>
    </div>

  </b-form>
</template>

<style lang="scss" scoped>
  #create-app-form {
    .logo-container {
      width: 50px; height: 50px;
      display: grid; place-items: center;

      > img {
        width: 28px;
      }
    }
  }
</style>

<script lang="ts">
  import {Component, Emit, Prop, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";

  import * as customerService from "@/service/customerService";
  import * as appNamesService from "@/service/appNamesService";
  import * as validationUtils from "@/service/validationUtils";
  import * as util from "@/service/util";


  @Component
  export default class CreateAppModal extends BaseComponent {

    @Prop({ required: true }) modalId: string;

    name = "";
    domainName = "";

    logo = "";
    logoFile: File = null;

    selectedCustomer = null;
    customerTypeaheadOptions = [];
    doSearchCustomerDebounced = _.noop;

    formErrorMsg = "";
    isSubmitting = false;

    // Emit decorators only
    @Emit("submitted") __submitted() {}


    mounted() {
      this.doSearchCustomerDebounced = _.debounce(async (q: string, loading: (p: boolean) => void) => {
        try {
          if (q.length < 2) {
            this.customerTypeaheadOptions = [];
          } else {
            loading(true);
            this.customerTypeaheadOptions = (await customerService.getCustomers({ q, page: 1, pageSize: 10 })).data;
            for (const customer of this.customerTypeaheadOptions) {
              const { customerCode, name, email, phoneNumber } = customer;
              customer.label = util.joinStrings([customerCode, name, email, phoneNumber], " - ");
            }
          }

        } catch (e) {
          this.toastError(e);

        } finally {
          loading(false);
        }
      }, 300);
    }

    onSelectCustomer(customer: any) {
      this.selectedCustomer = customer;
    }

    async onFileInput(file: File) {
      this.logo = file? await util.fileToBase64(file) : null;
    }

    onClickCancel() {
      this.$bvModal.hide(this.modalId);
    }

    async submitForm() {
      this.formErrorMsg = "";

      try {
        this.isSubmitting = true;

        const logoExt = this.logoFile.name.slice(this.logoFile.name.lastIndexOf(".")).replace(".", "");

        const data = {
          name: this.name,
          domainName: this.domainName,
          logo: this.logo,
          logoExt,
          active: true,
          customerId: this.selectedCustomer?.id,
          customerName: this.selectedCustomer?.name
        };
        await appNamesService.create(data);

        this.__submitted();
        this.toastSuccessDelay(this.$t("createAppSuccessful").toString());
        this.$bvModal.hide(this.modalId);

      } catch (e) {
        this.toastError(e);
      } finally {
        this.isSubmitting = false;
      }
    }

  }
</script>
